/**
 * **旁观牌扇 / 取牌扇** —— 一个**常驻**的组件，平时渲染为空。
 *
 * 它是 `CardStrip`（一扇牌背，和手牌同一套 `CardView` + `CardRig`）+ 一张
 * **与牌背同姿态的 `CardView`**（拖到会打出时才翻出来的牌面）。两种用法：
 *
 * - **旁观**：`setCount(n)` + `setHover/setDrag/reorder/setReveal`，
 *   镜像本回合行动者的手牌（只进位次，不进身份）；
 * - **取牌**：`setPick({ count, onPick })`，把同一扇牌背变成可点的一手
 *   （抽牌对峙）。
 *
 * ⚠️ 本组件**不负责**摆在屏幕哪里、朝哪边转 —— 那是调用方的版面。
 *    联机里整层绕画布中心转 180° 呈现到顶部，热座里摆在桌面正中倒挂。
 */
import { Container } from 'pixi.js';
import type { Renderer, Ticker } from 'pixi.js';
import type { CardRegistry } from '../../core/pack';
import { faceSize, type FaceSize } from '../card/metrics';
import type { CardFaceCache } from '../card/faceCache';
import { CardStrip } from './cardStrip';
import { CardView } from '../card/cardView';

/** 翻牌半程时长（毫秒）——和 `CardFlip` 的两半一致 */
const FLIP_MS = 110;

export interface SpectatorFanOptions {
  readonly renderer: Renderer;
  readonly registry: CardRegistry;
  /** 共享牌面缓存（按下露牌那张脸走它） */
  readonly faces: CardFaceCache;
  /** 牌高（逻辑像素）。和正常手牌同一套（调用方传 HAND_HEIGHT） */
  readonly height?: number;
  readonly maxWidth?: number;
  readonly maxSpread?: number;
}

export class SpectatorFan {
  /** 加进场景的层。位置 / 旋转由调用方给 */
  readonly view: Container;

  readonly #strip: CardStrip;
  readonly #registry: CardRegistry;
  readonly #faces: CardFaceCache;
  readonly #size: FaceSize;

  /**
   * 翻出来的那张**牌面** —— 一个和牌背**同姿态**的 `CardView`。
   *
   * ⚠️ 它不是单独摆在一处的贴图：位置 / 旋转 / 透视 / 缩放都从对应槽位的
   *    牌背那里逐帧抄（见 `#syncRevealFlip`），所以拖动时严丝合缝地跟着走。
   */
  #face: CardView | null = null;
  #faceTitle: string | null = null;
  #faceSlot: number | null = null;
  /** 被拖的那张现在"松手就会打出"吗（只有这时才翻面） */
  #armed = false;
  /** 翻牌进度：0 = 背面朝外，1 = 正面朝外 */
  #flip = 0;
  #flipTarget = 0;

  #onPick: ((slot: number) => void) | undefined;
  #onHover: ((slot: number | null) => void) | undefined;

  constructor(options: SpectatorFanOptions) {
    this.#registry = options.registry;
    this.#faces = options.faces;
    const height = options.height ?? 120;
    this.#size = faceSize(height);
    // ★ **按正常牌扇渲染**（正常弧度、正常 hover/拖拽）。整层旋转 180° 由调用方完成。
    //   `pickable: true` 让槽位一开始就绑好监听；要不要响应由 `#pickMode` /
    //   外层 `eventMode` 决定（所以同一扇能在"旁观 / 取牌"之间来回切）。
    this.#strip = new CardStrip({
      renderer: options.renderer,
      height,
      maxSpread: options.maxSpread ?? 30,
      maxWidth: options.maxWidth ?? 640,
      maxTilt: 7,
      pickable: true,
      onHover: (slot) => this.#onHover?.(slot),
      onPick: (slot) => this.#onPick?.(slot),
    });

    this.view = new Container();
    this.view.label = 'spectator-fan';
    this.view.addChild(this.#strip.view);
    this.setEmpty();
  }

  /** 单张牌的尺寸（调用方排版 / 定 pivot 用） */
  get size(): FaceSize {
    return this.#size;
  }

  /** 诊断：翻牌进度 / 牌面可见性（测试与调试页读它） */
  get debugReveal(): { readonly flip: number; readonly target: number; readonly visible: boolean; readonly hasFace: boolean } {
    return { flip: this.#flip, target: this.#flipTarget, visible: this.#face?.visible ?? false, hasFace: this.#face !== null };
  }

  /**
   * **把牌扇放在这一层的某处。** 不传 = 原点。
   *
   * 只动牌扇那一层，按下露出的牌面也跟着走（它按槽位算，见 `setReveal`）。
   */
  setOffset(x: number, y: number): void {
    this.#strip.view.position.set(x, y);
  }

  /** **平时就这么放着**：节点还在，只是不画牌、不吃指针 */
  setEmpty(): void {
    this.#onHover = undefined;
    this.#onPick = undefined;
    this.#clearFace();
    this.#faceSlot = null;
    this.#armed = false;
    this.#strip.setItems([]);
    this.#strip.view.eventMode = 'none';
    this.view.visible = true;
  }

  /** 旁观：灌一张数，镜像别人的手牌（数据由旁路通道给） */
  setCount(count: number): void {
    this.#onHover = undefined;
    this.#onPick = undefined;
    this.#strip.view.eventMode = 'none';
    this.#strip.setCount(count);
  }

  /** 取牌：同一扇牌背变成可点的一手 */
  setPick(options: {
    readonly count: number;
    readonly onHover?: (slot: number | null) => void;
    readonly onPick?: (slot: number) => void;
  }): void {
    this.#onHover = options.onHover;
    this.#onPick = options.onPick;
    this.#clearFace();
    this.#faceSlot = null;
    this.#strip.setCount(options.count);
    this.#strip.view.eventMode = 'passive';
  }

  /** 开 / 关指针（记牌器模态时整扇不吃指针） */
  setInteractive(on: boolean): void {
    this.#strip.view.eventMode = on ? 'passive' : 'none';
  }

  setHover(slot: number | null): void {
    this.#strip.highlight = slot;
  }

  /**
   * `slot === null` = 松手，收掉位移。
   *
   * `armed` = 从行动者那边传来的"松手会打出"（越过出牌线）—— 只有这时才翻面。
   */
  setDrag(slot: number | null, x = 0, y = 0, armed = false, title: string | null = null): void {
    // 牌名随拖拽帧一起来了 → 翻面不依赖 SIDE_REVEAL 那一帧
    if (title !== null && slot !== null && title !== this.#faceTitle) {
      this.setReveal(slot, title);
    }
    // 正常（不取反）——整层的 180° 旋转已经处理了方向
    this.#strip.setRemoteDrag(slot, x, y);
    // 行动者给的 armed 是权威判据；`y` 是兜底 —— 出牌线在扇心上方约
    // 0.77 个牌高，拖过去就是"松手会打出"。
    this.#armed = slot !== null && (armed || y <= -0.77);
    if (slot !== null) this.#faceSlot = slot;
    this.#flipTarget = this.#armed && this.#face !== null ? 1 : 0;
  }

  /** 重排：把第 `from` 位挪到第 `to` 位 */
  reorder(from: number, to: number): void {
    this.#strip.reorder(from, to);
  }

  /**
   * 按下透露牌面。`slot/title` 任一为 null = 收回。
   *
   * 注意：**按下本身不翻面** —— 要不要翻由 `setDrag(..., armed)` 决定。
   */
  setReveal(slot: number | null, title: string | null): void {
    if (slot === null || title === null) {
      // ⚠️ 不立刻清掉 #faceSlot —— 先让翻牌动画把面翻回去，归零后再清
      this.#armed = false;
      this.#flipTarget = 0;
      return;
    }
    this.#faceSlot = slot;
    if (title !== this.#faceTitle) {
      this.#clearFace();
      this.#buildFace(title);
    }
    if (this.#face !== null) {
      this.#face.visible = this.#flip > 0.001;
      if (this.#armed) this.#flipTarget = 1;
    }
  }

  #buildFace(title: string): void {
    const def = this.#registry.get(title as never);
    if (def === undefined) return;
    // ★ 和手牌同一套：保留有意设计的破框（不裁出血）
    const face = new CardView({ texture: this.#faces.get(def, this.#size), size: this.#size });
    face.visible = false;
    this.#face = face;
    this.#faceTitle = title;
    this.view.addChild(face);
  }

  #clearFace(): void {
    if (this.#face !== null) {
      this.#face.destroy({ children: true });
      this.#face = null;
    }
    this.#faceTitle = null;
    this.#flip = 0;
    this.#flipTarget = 0;
  }

  /**
   * **翻牌 + 跟随牌背。**
   *
   * 牌面的姿态逐帧抄对应槽位的牌背（位置 / 旋转 / 透视 / 缩放），再把两张牌
   * 沿竖直轴压扁：前半段牌背 1→0，后半段牌面 0→1 —— 就是别处那套翻牌效果。
   * 牌背节点**不删**，只是临时被压扁；拖回去就反向翻回。
   */
  #syncRevealFlip(deltaMS: number): void {
    const face = this.#face;
    const slot = this.#faceSlot;
    if (face === null || slot === null) return;
    const back = this.#strip.slotView(slot) as CardView | null;
    // ⚠️ 槽位暂时不存在（张数还没到）时**不要收掉牌面** —— 收了之后
    //    后续再也不会 `setReveal`（位次没变），翻面就永远不触发。
    if (back === null) return;
    // 自愈：只要还处于"会打出"态就保持翻面目标（不管事件到达顺序）
    if (this.#armed) this.#flipTarget = 1;

    const p = back.pose;
    face.setPose({ ...p, x: this.#strip.view.x + p.x, y: this.#strip.view.y + p.y });

    // 半程 FLIP_MS，两半加起来一个完整翻面
    const d = (deltaMS / FLIP_MS) * 0.5;
    if (this.#flip < this.#flipTarget) this.#flip = Math.min(this.#flipTarget, this.#flip + d);
    else if (this.#flip > this.#flipTarget) this.#flip = Math.max(this.#flipTarget, this.#flip - d);

    // 翻回背面走完了 → 收掉牌面，牌背恢复原样（这一帧别再压它）
    if (this.#flipTarget === 0 && this.#flip <= 0.001) {
      this.#clearFace();
      this.#faceSlot = null;
      return;
    }

    const backK = this.#flip < 0.5 ? 1 - this.#flip * 2 : 0;
    const faceK = this.#flip < 0.5 ? 0 : (this.#flip - 0.5) * 2;
    back.scale.x = p.scale * backK;
    face.scale.x = p.scale * faceK;
    face.visible = faceK > 0.001;
  }

  update(ticker: Ticker): void {
    this.#strip.update(ticker);
    this.#syncRevealFlip(ticker.deltaMS);
  }

  /** 宿主只有毫秒时用这个（`update` 要 Ticker 只是历史） */
  updateFrame(deltaMS: number): void {
    this.#strip.step(deltaMS);
    this.#syncRevealFlip(deltaMS);
  }

  pulse(): void {
    this.#strip.pulse();
  }

  destroy(): void {
    this.#clearFace();
    this.#strip.destroy();
    this.view.destroy();
  }
}
